"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import { usePublicClient } from "wagmi";
import { Button } from "@/components/ui/button";
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipProvider,
  HybridTooltipTrigger,
} from "@/components/ui/hybrid-tooltip";
import type { ExtendedUserStake } from "@/hooks/stake/useFetchStakeContractData";
import { useInvalidateStakeReads } from "@/hooks/stake/useInvalidateStakeReads";
import { useWithdrawStake } from "@/hooks/stake/useStakeWriteCalls";
import { useTxToasts } from "@/hooks/stake/useTxToasts";
import { useLongTxHint } from "@/hooks/useLongTxHint";

interface WithdrawStakeButtonProps {
  stake: ExtendedUserStake;
}

export const WithdrawStakeButton: React.FC<WithdrawStakeButtonProps> = ({
  stake,
}) => {
  const t = useTranslations("stake");
  const publicClient = usePublicClient();
  const invalidateStakeReads = useInvalidateStakeReads();
  const { showSuccess, showError } = useTxToasts();

  const disabled = !stake.is_matured;
  const claimResidual = stake.drip_net_uvm_wei > 0n;

  const { execute, isPending } = useWithdrawStake({
    stakeId: BigInt(stake.stake_id),
    claimResidual,
  });

  useLongTxHint(isPending);

  const handleClick = async () => {
    if (!publicClient) return;
    try {
      const hash = await execute();
      await publicClient.waitForTransactionReceipt({ hash });
      invalidateStakeReads();
      showSuccess(t("card.withdrawSuccess"));
    } catch (err) {
      showError(err, t("card.withdrawError"));
    }
  };

  const button = (
    <Button
      type="button"
      variant={disabled ? "flat" : "solid"}
      colors="secondary"
      disabled={disabled || isPending}
      onClick={handleClick}
    >
      {isPending ? t("card.withdrawingStake") : t("card.withdrawStake")}
    </Button>
  );

  if (disabled) {
    return (
      <HybridTooltipProvider>
        <HybridTooltip>
          <HybridTooltipTrigger asChild>
            <span>{button}</span>
          </HybridTooltipTrigger>
          <HybridTooltipContent
            side="bottom"
            className="max-w-64 text-xs text-center"
          >
            {t("card.withdrawBlocked")}
          </HybridTooltipContent>
        </HybridTooltip>
      </HybridTooltipProvider>
    );
  }

  return button;
};
